import { type ParsedLocation, type RouterEvents } from '@tanstack/react-router';

import casinoConfig from '@dcentralexpedientedigital/config';
import { isBlocks } from '@dcentralexpedientedigital/fe-casino/src/utils/is-blocks';
import type { IStaticBlocksData } from '@dcentralexpedientedigital/fe-shared/types/config.interface';

import type { router as ourRouter } from '../routes';

import { userManager } from './user-manager';

export interface UrlSearchSchema {
    modal: string;
}

interface FlowsConfig {
    apiUrl: string;
    routeId?: string;
}

/**
 * Make POST request to flows API
 */
const triggerFlow = async (apiUrl: string, flowId: string): Promise<void> => {
    const user = await userManager.getUser();
    const token = user?.access_token ?? null;
    const url = `${apiUrl}/${flowId}`;

    try {
        await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
        });
    } catch (error) {
        console.error('Error triggering flow:', error);
    }
};

export const initializeFlowsHandler = (flows: FlowsConfig, router: typeof ourRouter) => {
    const getRoute = (pathname: string, search: Record<string, string> | UrlSearchSchema) => {
        const matchedRoutes = router.matchRoutes(pathname, search);
        const match = matchedRoutes.find((m) => isBlocks(m.staticData));
        return match ? { ...match, staticData: match.staticData as IStaticBlocksData } : undefined;
    };

    const handleOnResolved = async (event: RouterEvents['onBeforeLoad']) => {
        const { toLocation, fromLocation } = event;
        const { pathname: toPath, search } = toLocation as ParsedLocation<UrlSearchSchema>;
        const route = getRoute(toPath, search);

        if (route?.staticData?.id === casinoConfig.flows.routeId) {
            if (!route?.params?.flowId) {
                console.error('Flow ID not found in pathname:', toPath);
                return;
            }

            // Redirect back to previous page
            router.navigate({
                to: fromLocation?.pathname ?? '/',
                search: fromLocation?.search,
                replace: true,
            });

            // Trigger the flow POST request with extracted flowId
            await triggerFlow(flows.apiUrl, route?.params?.flowId);
        }
    };

    const unsubscribe = router.subscribe('onBeforeLoad', handleOnResolved);

    return {
        cleanup: () => {
            unsubscribe();
        },
    };
};
